import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { AlertCircle, Loader2, RefreshCw, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { VoiceMicButton } from './VoiceMicButton';
import { VoiceWaveform } from './VoiceWaveform';
import { VoiceTranscript } from './VoiceTranscript';
import { VoiceResponseCard } from './VoiceResponseCard';
import { useVoiceAssistant } from './useVoiceAssistant';
import { type VoiceContext, getContextConfig } from './voiceContextPrompts';

// ─── Props ─────────────────────────────────────────────────────────────────────

interface VoiceAssistantOverlayProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context?: VoiceContext;
}

// ─── Component ─────────────────────────────────────────────────────────────────

export function VoiceAssistantOverlay({
  open,
  onOpenChange,
  context = { type: 'general' },
}: VoiceAssistantOverlayProps) {
  const { i18n } = useTranslation();
  const isFr = i18n.language.startsWith('fr');

  const {
    phase,
    transcript,
    responseText,
    errorMessage,
    isSpeaking,
    isSupported,
    startListening,
    stopListening,
    stopSpeaking,
    greet,
    retry,
    reset,
  } = useVoiceAssistant({ context, open, onClose: () => onOpenChange(false) });

  // Speak greeting once when overlay opens.
  // Using a short timeout ensures it fires within the user-gesture window
  // (the long-press that triggered the overlay) while React has finished rendering.
  const greetedRef = useRef(false);
  useEffect(() => {
    if (open && !greetedRef.current) {
      greetedRef.current = true;
      const timer = setTimeout(() => greet(), 100);
      return () => clearTimeout(timer);
    }
    if (!open) {
      greetedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Don't render if speech APIs are unavailable
  if (!isSupported) return null;

  // ── Derived state ────────────────────────────────────────────────────────────
  const isListening = phase === 'listening';
  const isProcessing = phase === 'processing';
  const showGreeting = !responseText && phase === 'idle';
  const showResponse = !!responseText && !isListening;

  const greeting = getContextConfig(context).greeting;
  const greetingText = isFr ? greeting.fr : greeting.en;

  // ── Event handlers ───────────────────────────────────────────────────────────
  const handleMicPress = () => {
    if (isListening) {
      stopListening();
    } else {
      // idle or responding — stop any ongoing speech then start listening
      stopSpeaking();
      startListening();
    }
  };

  const handleClose = () => {
    reset();
    onOpenChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  // ── Footer hint text ─────────────────────────────────────────────────────────
  const footerHint = isListening
    ? isFr
      ? 'Parlez maintenant...'
      : 'Speak now...'
    : phase === 'idle'
      ? isFr
        ? 'Appuyez sur le micro pour parler'
        : 'Tap the mic to speak'
      : '';

  // ── Mic button state ─────────────────────────────────────────────────────────
  const micState = isListening ? 'listening' : isProcessing ? 'processing' : 'idle';

  // ── Overlay markup ───────────────────────────────────────────────────────────
  const overlay = (
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label={isFr ? 'Assistant vocal' : 'Voice assistant'}
          tabIndex={-1}
          onKeyDown={handleKeyDown}
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.97 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className={cn('fixed inset-0 z-50 flex flex-col', 'bg-white dark:bg-charcoal-950')}
        >
          {/* ── Header ── */}
          <header
            className="flex items-start justify-end px-4"
            style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)' }}
          >
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              aria-label={isFr ? 'Fermer' : 'Close'}
            >
              <X className="size-5" />
            </Button>
          </header>

          {/* ── Main ── */}
          <main className="flex flex-1 flex-col items-center justify-center gap-6 px-4">
            {/* Greeting text */}
            <AnimatePresence>
              {showGreeting && (
                <motion.p
                  key="greeting"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.25 }}
                  className="font-heading text-lg font-semibold text-charcoal-900 dark:text-neutral-100 text-center"
                >
                  {greetingText}
                </motion.p>
              )}
            </AnimatePresence>

            {/* Response card */}
            <AnimatePresence>
              {showResponse && (
                <VoiceResponseCard
                  key="response-card"
                  text={responseText}
                  isSpeaking={isSpeaking}
                  onStopSpeaking={stopSpeaking}
                  className="w-full max-w-md"
                />
              )}
            </AnimatePresence>

            {/* Processing indicator */}
            <AnimatePresence>
              {isProcessing && (
                <motion.div
                  key="processing"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                  className="flex flex-col items-center gap-3"
                >
                  <Loader2 className="size-6 animate-spin text-lime-500" />
                  <p className="text-sm text-charcoal-500 dark:text-neutral-400">
                    {isFr ? "L'assistant réfléchit..." : 'Thinking...'}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Error card */}
            <AnimatePresence>
              {errorMessage && (
                <motion.div
                  key="error"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.3 }}
                  className="mx-6 flex flex-col items-center gap-4 rounded-lg border-[length:var(--border-brutal)] border-error bg-error-light dark:bg-error-dark/20 p-5 shadow-[3px_3px_0_#991B1B] max-w-md"
                >
                  <AlertCircle className="size-8 text-error" />
                  <p className="text-center text-sm text-error-dark dark:text-error-light leading-relaxed">
                    {errorMessage}
                  </p>
                  <Button variant="outline" size="sm" onClick={retry} className="gap-2">
                    <RefreshCw className="size-3.5" />
                    {isFr ? 'Reessayer' : 'Try again'}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Mic button — hidden while processing or error */}
            {!isProcessing && !errorMessage && (
              <VoiceMicButton state={micState} onPress={handleMicPress} />
            )}

            {/* Waveform — active only when listening */}
            <VoiceWaveform active={isListening} />

            {/* Live transcript */}
            <VoiceTranscript text={transcript} />
          </main>

          {/* ── Footer ── */}
          <footer
            className="flex items-end justify-center px-4 text-center"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
          >
            <AnimatePresence mode="wait">
              {footerHint ? (
                <motion.p
                  key={footerHint}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="text-xs text-charcoal-400 dark:text-neutral-500"
                >
                  {footerHint}
                </motion.p>
              ) : (
                // Invisible spacer to preserve footer height
                <span key="empty" className="text-xs opacity-0" aria-hidden="true">
                  &nbsp;
                </span>
              )}
            </AnimatePresence>
          </footer>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return typeof document !== 'undefined' ? createPortal(overlay, document.body) : overlay;
}
